import React, {useEffect, useRef, useState} from "react";
import {apiAdminGenerateNewsDraft} from "../api/news";
import Modal from "./Modal";
import NewsContentRenderer from "./NewsContentRenderer";
import {C} from "../styles";
import {useConfirm} from "./FeedbackProvider";

const INTERACTION_OPTIONS = [
    {value: "let_ai_decide", label: "Laisser l'IA proposer"},
    {value: "question", label: "Question aux agents"},
    {value: "poll", label: "Sondage"},
    {value: "ideas", label: "Appel à idées"},
    {value: "none", label: "Aucune"},
];

const INTERACTION_TYPE_LABELS = {question: "Question", poll: "Sondage", ideas: "Appel à idées"};

const fieldStyle = {width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 4, fontSize: 14};
const labelStyle = {fontSize: 13, fontWeight: 600, color: C.text, display: "block", marginTop: 12, marginBottom: 4};
const buttonStyle = {border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontSize: 14};
const suggestionBoxStyle = {backgroundColor: C.primaryLight, borderRadius: 8, padding: 12, marginTop: 12};
const fieldErrorStyle = {color: C.danger, fontSize: 12};

const INITIAL_FORM = {subject: "", sourceText: "", sappmPosition: "", importantPoints: "", interactionMode: "let_ai_decide"};

export default function NewsAiAssistant({hasExistingContent, onInsert, onGeneratingChange}) {
    const confirm = useConfirm();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(INITIAL_FORM);
    const [generating, setGenerating] = useState(false);
    const [draft, setDraft] = useState(null);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    // Verrou synchrone : `generating` n'est à jour qu'après re-render, trop tard pour un double-clic rapide.
    const generatingRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const canGenerate = form.subject.trim().length >= 3 && form.sourceText.trim().length > 0 && !generating;
    const hasUnsavedWork = Boolean(form.subject.trim() || form.sourceText.trim() || draft);

    function updateForm(field) {
        return (e) => {
            const value = e.target.value;
            setForm(f => ({...f, [field]: value}));
        };
    }

    function resetAndClose() {
        setOpen(false);
        setForm(INITIAL_FORM);
        setDraft(null);
        setError("");
        setFieldErrors({});
    }

    async function handleRequestClose() {
        if (generating) return;
        if (hasUnsavedWork && !(await confirm("Fermer sans insérer le brouillon ? Les informations saisies seront perdues.", {danger: true}))) return;
        resetAndClose();
    }

    function updateGenerating(value) {
        generatingRef.current = value;
        setGenerating(value);
        onGeneratingChange?.(value);
    }

    async function handleGenerate() {
        if (generatingRef.current) return;
        updateGenerating(true);
        setError("");
        setFieldErrors({});
        const result = await apiAdminGenerateNewsDraft({
            subject: form.subject.trim(),
            sourceText: form.sourceText.trim(),
            sappmPosition: form.sappmPosition.trim(),
            importantPoints: form.importantPoints.trim(),
            interactionMode: form.interactionMode,
        });
        // Assistant démonté pendant la requête : plus rien à afficher ni à signaler.
        if (!mountedRef.current) return;
        updateGenerating(false);
        if (result.error) {
            if (result.fields) setFieldErrors(result.fields);
            setError(result.message || result.error);
            return;
        }
        setDraft(result);
    }

    async function handleInsert() {
        if (hasExistingContent && !(await confirm("Ceci va remplacer le titre, le résumé et le contenu actuels de l'article. Continuer ?", {danger: true}))) return;
        onInsert({title: draft.title, excerpt: draft.excerpt, contentJson: JSON.parse(draft.content_json)});
        resetAndClose();
    }

    if (!open) {
        return (
            <button type="button" onClick={() => setOpen(true)} style={{...buttonStyle, backgroundColor: C.primaryLight}}>
                ✨ Préparer avec l'IA
            </button>
        );
    }

    return (
        <Modal title="Assistant IA de rédaction" onClose={handleRequestClose} scrollable>
            <label style={labelStyle} htmlFor="ai-subject">Sujet</label>
            <input id="ai-subject" style={fieldStyle} maxLength={150} value={form.subject} onChange={updateForm("subject")}/>
            {fieldErrors.subject && <p style={fieldErrorStyle}>{fieldErrors.subject}</p>}

            <label style={labelStyle} htmlFor="ai-source">Informations / sources</label>
            <textarea id="ai-source" style={{...fieldStyle, minHeight: 100}} maxLength={20000} value={form.sourceText} onChange={updateForm("sourceText")}/>
            {fieldErrors.sourceText && <p style={fieldErrorStyle}>{fieldErrors.sourceText}</p>}
            <p style={{fontSize: 12, color: C.textMuted, margin: "0 0 4px"}}>
                Ne collez pas de données personnelles, de situations individuelles confidentielles ou d'informations sensibles.
            </p>

            <label style={labelStyle} htmlFor="ai-position">Position du SAPPM (facultatif)</label>
            <textarea id="ai-position" style={fieldStyle} placeholder="À déterminer / aucune position arrêtée" maxLength={2000} value={form.sappmPosition} onChange={updateForm("sappmPosition")}/>
            {fieldErrors.sappmPosition && <p style={fieldErrorStyle}>{fieldErrors.sappmPosition}</p>}

            <label style={labelStyle} htmlFor="ai-points">Points importants (facultatif)</label>
            <textarea id="ai-points" style={fieldStyle} maxLength={2000} value={form.importantPoints} onChange={updateForm("importantPoints")}/>
            {fieldErrors.importantPoints && <p style={fieldErrorStyle}>{fieldErrors.importantPoints}</p>}

            <label style={labelStyle} htmlFor="ai-interaction">Interaction souhaitée</label>
            <select id="ai-interaction" style={fieldStyle} value={form.interactionMode} onChange={updateForm("interactionMode")}>
                {INTERACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {error && <p role="alert" style={{color: C.danger, fontSize: 13, marginTop: 12}}>{error}</p>}

            <div style={{display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap"}}>
                <button type="button" disabled={!canGenerate} onClick={handleGenerate} style={{...buttonStyle, opacity: canGenerate ? 1 : 0.5}}>
                    {generating ? "Génération en cours..." : "Générer un brouillon"}
                </button>
            </div>

            {draft && (
                <div style={{marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 16}}>
                    <h4 style={{margin: "0 0 8px", color: C.primaryDark}}>{draft.title}</h4>
                    <p style={{fontStyle: "italic", color: C.textMuted}}>{draft.excerpt}</p>
                    <NewsContentRenderer document={JSON.parse(draft.content_json)}/>

                    {draft.keyPoints && draft.keyPoints.length > 0 && (
                        <div style={suggestionBoxStyle}>
                            <strong>À retenir (suggestion)</strong>
                            <ul>{draft.keyPoints.map((p, i) => <li key={i}>{p}</li>)}</ul>
                        </div>
                    )}

                    {draft.interaction && draft.interaction.type !== "none" && (
                        <div style={suggestionBoxStyle}>
                            <strong>Interaction proposée (suggestion) — {INTERACTION_TYPE_LABELS[draft.interaction.type] || draft.interaction.type}</strong>
                            <p style={{margin: "4px 0 0"}}>{draft.interaction.question}</p>
                            {draft.interaction.options && draft.interaction.options.length > 0 && (
                                <ul>{draft.interaction.options.map((o, i) => <li key={i}>{o}</li>)}</ul>
                            )}
                        </div>
                    )}

                    {draft.editorNote && (
                        <div style={{...suggestionBoxStyle, backgroundColor: C.accentLight}}>
                            <strong>Note interne — ne sera jamais publiée</strong>
                            <p style={{margin: "4px 0 0"}}>{draft.editorNote}</p>
                        </div>
                    )}

                    <div style={{display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap"}}>
                        <button type="button" onClick={handleInsert} style={{...buttonStyle, backgroundColor: C.primary, color: "#fff"}}>
                            Insérer dans l'article
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
}
